import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import vendorApi from '../../services/vendorApi';
import { MapPin, ChevronRight } from 'lucide-react';
import { AdminPageHeader, adminCardClass } from '../../components/admin/AdminShared';
import ResponsiveImage from '../../components/ResponsiveImage';

const VendorListings = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated || !user) {
        navigate('/vendor/login', { replace: true });
        return;
      }
      if (user.role !== 'vendor' && user.role !== 'admin') {
        navigate('/vendor/login', { replace: true, state: { needsVendorAccount: true } });
        return;
      }
    }
  }, [user, authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchStays = async () => {
      try {
        setLoading(true);
        const response = await vendorApi.getStays();
        setStays(response.stays || []);
      } catch (error) {
        console.error('Error fetching vendor stays:', error);
        setStays([]);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && user && (user.role === 'vendor' || user.role === 'admin')) {
      fetchStays();
    }
  }, [isAuthenticated, user]);

  const getStartingPrice = (stay) => {
    if (!stay.rooms || stay.rooms.length === 0) {
      return null;
    }

    const prices = stay.rooms
      .map((room) => {
        if (room.price) {
          return room.price.discountedPrice || room.price.mrp || 0;
        }
        return 0;
      })
      .filter((price) => price > 0);

    if (prices.length === 0) {
      return null;
    }

    return Math.min(...prices);
  };

  const formatLocation = (stay) => {
    const parts = [];
    if (stay.city) parts.push(stay.city);
    if (stay.state) parts.push(stay.state);
    return parts.join(', ') || 'Location not specified';
  };

  if (authLoading || loading || !isAuthenticated || !user || (user.role !== 'vendor' && user.role !== 'admin')) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#FF5E4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader
        title="Listings"
        subtitle="Your stays on Book For Paws."
      >
        <button
          type="button"
          onClick={() => navigate('/vendor/listings/new')}
          className="inline-flex items-center justify-center rounded-full bg-[#FF5E4C] text-white text-[14px] font-medium px-5 py-2.5 hover:opacity-90 transition-opacity shrink-0"
        >
          Create new
        </button>
      </AdminPageHeader>

      {stays.length === 0 ? (
        <div className={`${adminCardClass} py-12 text-center`}>
          <p className="mb-4 text-[#78736A]">No listings yet</p>
          <button
            type="button"
            onClick={() => navigate('/vendor/listings/new')}
            className="rounded-full bg-[#FF5E4C] px-6 py-2.5 text-white text-[14px] font-medium transition-opacity hover:opacity-90"
          >
            Create your first listing
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {stays.map((stay) => {
            const startingPrice = getStartingPrice(stay);
            const location = formatLocation(stay);

            return (
              <button
                type="button"
                key={stay.stayId}
                onClick={() => navigate(`/vendor/listings/${stay.stayId}`)}
                className={`w-full text-left ${adminCardClass} p-4 md:p-5 hover:shadow-md transition-shadow`}
              >
                <div className="flex items-start gap-4">
                  <div className="h-24 w-24 shrink-0 overflow-hidden rounded-[12px] bg-[#FAF7F2] border border-[#E5E3DF]">
                    {stay.images && stay.images.length > 0 ? (
                      <ResponsiveImage
                        src={stay.images[0]}
                        variant="thumb96"
                        alt={stay.title}
                        className="h-full w-full object-cover object-center"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <span className="text-xs text-[#78736A]">No image</span>
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h3 className="mb-1 line-clamp-1 text-[15px] font-medium text-black">
                      {stay.title || 'Untitled stay'}
                    </h3>
                    <div className="mb-2 flex items-center text-[13px] text-[#5c5c5c]">
                      <MapPin className="mr-1 h-4 w-4 shrink-0 text-[#FF5E4C]" />
                      <span className="truncate">{location}</span>
                    </div>
                    {startingPrice !== null && (
                      <div className="mt-1">
                        <p className="text-[12px] text-[#78736A]">Starting from</p>
                        <p className="text-lg font-medium text-[#FF5E4C] tabular-nums">
                          ₹{startingPrice.toLocaleString('en-IN')}
                        </p>
                      </div>
                    )}
                  </div>

                  <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-[#78736A]" aria-hidden />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VendorListings;
